<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router/auto'
import type { ProviderAccountType } from '@/types'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  // `signupOnly`: con el alta cerrada (el default) rebota a login — la cuenta
  // se pide por mail y se completa desde el link de la invitación.
  meta: { layout: 'blank', public: true, publicOnly: true, signupOnly: true },
})

const router = useRouter()

/**
 * Avanza de paso y **avisa si la navegación no ocurrió**.
 *
 * `router.push` no lanza: devuelve un `NavigationFailure` que, ignorado, deja
 * un botón que responde y no pasa nada — sin rastro en consola ni en pantalla.
 * Nos costó una sesión entera de diagnóstico a ciegas.
 */
const go = async (to: RouteLocationRaw) => {
  const failure = await router.push(to)

  if (failure)
    console.error('[register] navigation blocked:', failure)

  return failure
}

const options: { type: ProviderAccountType; icon: string; title: string; desc: string }[] = [
  {
    type: 'company',
    icon: 'ri-building-line',
    title: 'Company',
    desc: 'Dive school or center. Multi-location, multi-user.',
  },
  {
    type: 'individual',
    icon: 'ri-user-line',
    title: 'Individual',
    desc: 'Instructor or guide. One dive center you manage yourself.',
  },
]

/*
 * El tipo de cuenta viaja en sessionStorage entre los 4 pasos del alta: recién
 * en el último se hace el POST /auth/register con todo junto.
 */
const pick = (type: ProviderAccountType) => {
  sessionStorage.setItem('bt_register_account_type', type)
  void go({ name: 'register-email' })
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper"
    no-gutters
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            How will you use Blue Trails?
          </h4>
          <p class="mb-0">
            Pick the option that fits you best — you can change details later.
          </p>
        </VCardText>

        <VCardText>
          <VRow>
            <VCol
              v-for="opt in options"
              :key="opt.type"
              cols="12"
            >
              <VCard
                variant="outlined"
                class="pa-4 cursor-pointer"
                @click="pick(opt.type)"
              >
                <div class="d-flex align-center gap-x-4">
                  <VAvatar
                    color="primary"
                    variant="tonal"
                    size="48"
                    rounded="lg"
                  >
                    <VIcon
                      :icon="opt.icon"
                      size="26"
                    />
                  </VAvatar>

                  <div class="flex-grow-1">
                    <h6 class="text-h6">
                      {{ opt.title }}
                    </h6>
                    <p class="mb-0 text-body-2">
                      {{ opt.desc }}
                    </p>
                  </div>

                  <VIcon
                    icon="ri-arrow-right-line"
                    class="text-medium-emphasis flip-in-rtl"
                  />
                </div>
              </VCard>
            </VCol>

            <VCol
              cols="12"
              class="text-center text-base"
            >
              <span>Already have an account?</span>
              <RouterLink
                class="text-primary d-inline-block ms-1"
                :to="{ name: 'login' }"
              >
                Log in
              </RouterLink>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
